import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatCurrency } from '../../utils/formatters';
import Button from '../ui/Button';
import { FileText, Wallet, Pencil, Trash2, ArrowUp, ArrowDown, Eye } from 'lucide-react';
import { Client, Supplier } from '../../types';
import InvoiceModal from '../modals/InvoiceModal';
import PaymentModal from '../modals/PaymentModal';
import EntityModal from '../modals/EntityModal';
import ConfirmModal from '../modals/ConfirmModal';
import { useAppStore } from '../../store';
import { supabase } from '../../lib/supabase';

interface EntityTableProps {
  data: Client[] | Supplier[];
  entityType: 'client' | 'supplier';
  onViewDetails: (id: string) => void;
  onEdit?: (entity: Client | Supplier) => void;
  /** Oculta facturación total y saldo (ej. rol Vendedor en clientes) */
  hideFinancialColumns?: boolean;
  /** Orden inicial por defecto (ej: balance desc para clientes con más saldo pendiente arriba) */
  defaultSortField?: SortField;
  defaultSortDirection?: SortDirection;
}

type SortField = 'name' | 'balance' | 'billing';
type SortDirection = 'asc' | 'desc';

const EntityTable: React.FC<EntityTableProps> = ({
  data,
  entityType,
  onViewDetails,
  onEdit,
  hideFinancialColumns = false,
  defaultSortField = 'name',
  defaultSortDirection = 'asc',
}) => {
  const navigate = useNavigate();
  const [selectedEntity, setSelectedEntity] = useState<string | null>(null);
  const [isInvoiceModalOpen, setIsInvoiceModalOpen] = useState(false);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<string | null>(null);
  const [sortField, setSortField] = useState<SortField>(defaultSortField);
  const [sortDirection, setSortDirection] = useState<SortDirection>(defaultSortDirection);
  
  const { updateClient, deleteClient, updateSupplier, deleteSupplier, setError, getEntityBalanceByCurrency, getEntityBillingByCurrency } = useAppStore();

  const computeBalancesByCurrency = useCallback((entityId: string, entityType: 'client' | 'supplier') => {
    return getEntityBalanceByCurrency(entityId, entityType);
  }, [getEntityBalanceByCurrency]);

  const computeBillingByCurrency = useCallback((entityId: string, entityType: 'client' | 'supplier') => {
    return getEntityBillingByCurrency(entityId, entityType);
  }, [getEntityBillingByCurrency]);
  
  const handleViewDetails = (id: string) => {
    if (entityType === 'client') {
      navigate(`/clientes/${id}`);
    } else {
      navigate(`/proveedores/${id}`);
    }
  };
  
  const handleAddInvoice = (id: string) => {
    setSelectedEntity(id);
    setIsInvoiceModalOpen(true);
  };
  
  const handleAddPayment = (id: string) => {
    setSelectedEntity(id);
    setIsPaymentModalOpen(true);
  };

  const handleEdit = (id: string) => {
    setSelectedEntity(id);
    
    // If external edit handler is provided, use it
    if (onEdit) {
      const entity = data.find(e => e.id === id);
      if (entity) {
        onEdit(entity);
      }
    } else {
      // Otherwise, open the edit modal directly
      setIsEditModalOpen(true);
    }
  };

  const handleDeleteClick = (id: string) => {
    setItemToDelete(id);
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!itemToDelete) return;
    
    try {
      if (entityType === 'supplier') {
        // First delete associated sample tasks
        const { error: sampleTasksError } = await supabase
          .from('sample_tasks')
          .delete()
          .eq('supplier_id', itemToDelete);

        if (sampleTasksError) {
          throw new Error(`Error deleting associated sample tasks: ${sampleTasksError.message}`);
        }

        // Then delete associated production tasks
        const { error: productionTasksError } = await supabase
          .from('production_tasks')
          .delete()
          .eq('supplier_id', itemToDelete);

        if (productionTasksError) {
          throw new Error(`Error deleting associated production tasks: ${productionTasksError.message}`);
        }

        // Finally delete the supplier
        await deleteSupplier(itemToDelete);
      } else {
        await deleteClient(itemToDelete);
      }
      
      setIsDeleteModalOpen(false);
      setItemToDelete(null);
    } catch (error: any) {
      console.error('Error al eliminar:', error);
      setError(error.message || 'Error al eliminar');
    }
  };
  
  const getEntityName = (id: string) => {
    const entity = data.find(e => e.id === id);
    return entity ? entity.name : '';
  };

  // Handle sorting
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const compareByCurrencyPair = (
    a: { ARS: number; USD: number },
    b: { ARS: number; USD: number }
  ) => {
    if (a.USD !== b.USD) return sortDirection === 'asc' ? a.USD - b.USD : b.USD - a.USD;
    return sortDirection === 'asc' ? a.ARS - b.ARS : b.ARS - a.ARS;
  };

  const showFinancialColumns = !(hideFinancialColumns && entityType === 'client');

  // Sort the data
  const sortedData = [...data].sort((a, b) => {
    if (sortField === 'name') {
      return sortDirection === 'asc'
        ? a.name.localeCompare(b.name)
        : b.name.localeCompare(a.name);
    }
    if (!showFinancialColumns) return 0;
    if (sortField === 'balance') {
      return compareByCurrencyPair(
        computeBalancesByCurrency(a.id, entityType),
        computeBalancesByCurrency(b.id, entityType)
      );
    }
    if (sortField === 'billing' && entityType === 'client') {
      return compareByCurrencyPair(
        computeBillingByCurrency(a.id, entityType),
        computeBillingByCurrency(b.id, entityType)
      );
    }
    return 0;
  });

  // Render sort indicator
  const renderSortIndicator = (field: SortField) => {
    if (sortField !== field) return null;
    
    return sortDirection === 'asc' 
      ? <ArrowUp className="h-4 w-4 inline ml-1" /> 
      : <ArrowDown className="h-4 w-4 inline ml-1" />;
  };

  return (
    <>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-800">
            <tr>
              <th 
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer"
                onClick={() => handleSort('name')}
              >
                Nombre {renderSortIndicator('name')}
              </th>
              {entityType === 'client' && showFinancialColumns && (
                <th
                  className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer"
                  onClick={() => handleSort('billing')}
                >
                  Facturación total {renderSortIndicator('billing')}
                </th>
              )}
              {showFinancialColumns && (
              <th 
                className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer"
                onClick={() => handleSort('balance')}
              >
                Saldo {renderSortIndicator('balance')}
              </th>
              )}
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Acciones
              </th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
            {sortedData.map((entity) => (
              <tr key={entity.id} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                  {entity.name}
                </td>
                {entityType === 'client' && showFinancialColumns && (
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-right">
                    {(() => {
                      const billing = computeBillingByCurrency(entity.id, entityType);
                      return (
                        <div className="space-y-0.5">
                          <div className={billing.ARS === 0 ? 'text-gray-500' : 'text-indigo-600 dark:text-indigo-400'}>
                            {formatCurrency(billing.ARS)} <span className="text-xs text-gray-500">(ARS)</span>
                          </div>
                          <div className={billing.USD === 0 ? 'text-gray-500' : 'text-indigo-600 dark:text-indigo-400'}>
                            {formatCurrency(billing.USD)} <span className="text-xs text-gray-500">(USD)</span>
                          </div>
                        </div>
                      );
                    })()}
                  </td>
                )}
                {showFinancialColumns && (
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-right">
                  {(() => {
                    const bal = computeBalancesByCurrency(entity.id, entityType);
                    return (
                      <div className="space-y-0.5">
                        <div className={bal.ARS === 0 ? 'text-gray-500' : (bal.ARS > 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400')}>
                          {formatCurrency(bal.ARS)} <span className="text-xs text-gray-500">(ARS)</span>
                        </div>
                        <div className={bal.USD === 0 ? 'text-gray-500' : (bal.USD > 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400')}>
                          {formatCurrency(bal.USD)} <span className="text-xs text-gray-500">(USD)</span>
                        </div>
                      </div>
                    );
                  })()}
                </td>
                )}
                <td className="px-6 py-4 whitespace-nowrap text-sm text-right">
                  <div className="flex justify-end space-x-2">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => handleViewDetails(entity.id)}
                    >
                      <Eye className="h-4 w-4 mr-1" />
                      Ver
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      icon={<Pencil className="h-4 w-4" />}
                      onClick={() => handleEdit(entity.id)}
                    >
                      Editar
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      icon={<Trash2 className="h-4 w-4" />}
                      onClick={() => handleDeleteClick(entity.id)}
                    >
                      Eliminar
                    </Button>
                    {entityType === 'supplier' && (
                      <>
                        <Button 
                          variant="outline" 
                          size="sm"
                          icon={<FileText className="h-4 w-4" />}
                          onClick={() => handleAddInvoice(entity.id)}
                        >
                          Compra
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          icon={<Wallet className="h-4 w-4" />}
                          onClick={() => handleAddPayment(entity.id)}
                        >
                          Pago
                        </Button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      
      {selectedEntity && (
        <>
          {entityType === 'supplier' && (
            <>
              <InvoiceModal
                isOpen={isInvoiceModalOpen}
                onClose={() => {
                  setIsInvoiceModalOpen(false);
                  setSelectedEntity(null);
                }}
                entity_id={selectedEntity}
                entity_type={entityType}
                entityName={getEntityName(selectedEntity)}
              />

              <PaymentModal
                isOpen={isPaymentModalOpen}
                onClose={() => {
                  setIsPaymentModalOpen(false);
                  setSelectedEntity(null);
                }}
                entityId={selectedEntity}
                entityName={getEntityName(selectedEntity)}
              />
            </>
          )}

          <EntityModal
            isOpen={isEditModalOpen}
            onClose={() => {
              setIsEditModalOpen(false);
              setSelectedEntity(null);
            }}
            entityType={entityType}
            onSave={async (updatedEntity) => {
              if (entityType === 'client') {
                await updateClient(selectedEntity, updatedEntity);
              } else {
                await updateSupplier(selectedEntity, updatedEntity);
              }
              setIsEditModalOpen(false);
              setSelectedEntity(null);
            }}
            initialData={data.find(e => e.id === selectedEntity) as Client | Supplier}
          />
        </>
      )}

      <ConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setItemToDelete(null);
        }}
        onConfirm={confirmDelete}
        title={`Eliminar ${entityType === 'client' ? 'Cliente' : 'Proveedor'}`}
        message={`¿Estás seguro de que deseas eliminar este ${entityType === 'client' ? 'cliente' : 'proveedor'}? Se eliminarán todas sus facturas, pagos y tareas asociadas.`}
        confirmText="Eliminar"
        variant="danger"
      />
    </>
  );
};

export default EntityTable;